import { RSAA } from 'redux-api-middleware'
import { getApiPath } from 'sidecar-utility-library'

import { types, initialState } from './types'

/**
 * Get the AI follow-up questions endpoint based on environment
 *
 * @returns {string} - API gateway endpoint URL
 */
const getAIFollowUpQuestionsEndpoint = () => {
  const environmentEndpoints = {
    prod: 'https://prod-api-gateway.sidecarhealth.com/patient-visit-data-analysis',
    qa: 'https://qa-api-gateway.sidecarhealth.com/patient-visit-data-analysis',
    dev: 'https://dev-api-gateway.sidecarhealth.com/patient-visit-data-analysis',
  }

  return environmentEndpoints[process.env.APP_ENV] || environmentEndpoints.dev
}

/**
 * Fetch AI follow-up questions for an expense
 *
 * @param {string} expenseUuid - UUID of the expense
 * @returns {object} RSAA action
 */
export const fetchAIFollowUpQuestions = expenseUuid => {
  const endpoint = `${getAIFollowUpQuestionsEndpoint()}/${expenseUuid}`

  return {
    [RSAA]: {
      endpoint: endpoint,
      method: 'GET',
      types: [
        types.FETCH_AI_FOLLOW_UP_QUESTIONS_REQUEST,
        {
          type: types.FETCH_AI_FOLLOW_UP_QUESTIONS_SUCCESS,
          meta: { expenseUuid },
        },
        types.FETCH_AI_FOLLOW_UP_QUESTIONS_ERROR,
      ],
    },
  }
}

/**
 * Save AI follow-up questions answers
 *
 * @param {string} expenseUuid - UUID of the expense
 * @param {object} answers - Object containing question IDs and their answers
 * @returns {object} RSAA action
 */
export const saveAIFollowUpAnswers = (expenseUuid, answers) => {
  // Transform answers object to formFields array format
  const formFields = Object.entries(answers).map(([questionId, answer]) => ({
    field: questionId,
    value: answer,
  }))

  return {
    [RSAA]: {
      endpoint: getApiPath('exp', `expenses/${expenseUuid}/expense-form`),
      method: 'PUT',
      body: JSON.stringify({
        formType: 'FOLLOW_UP',
        formFields,
      }),
      types: [
        types.SAVE_AI_FOLLOW_UP_ANSWERS_REQUEST,
        {
          type: types.SAVE_AI_FOLLOW_UP_ANSWERS_SUCCESS,
          meta: { expenseUuid },
        },
        types.SAVE_AI_FOLLOW_UP_ANSWERS_ERROR,
      ],
    },
  }
}

/**
 * Fetch AI follow-up questions from expense form
 *
 * @param {string} expenseUuid - UUID of the expense
 * @returns {object} RSAA action
 */
export const fetchAIFollowupAnswers = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${expenseUuid}/expense-form?formType=FOLLOW_UP`,
    ),
    method: 'GET',
    types: [
      types.FETCH_AI_FOLLOW_UP_ANSWERS_REQUEST,
      {
        type: types.FETCH_AI_FOLLOW_UP_ANSWERS_SUCCESS,
        meta: { expenseUuid },
      },
      types.FETCH_AI_FOLLOW_UP_ANSWERS_ERROR,
    ],
  },
})

/**
 * Clear AI follow-up questions error
 *
 * @returns {object} Action
 */
export const clearAIFollowUpQuestionsError = () => ({
  type: types.CLEAR_AI_FOLLOW_UP_QUESTIONS_ERROR,
})

/**
 * Clear AI follow-up questions data
 *
 * @returns {object} Action
 */
export const clearAIFollowUpQuestionsData = () => ({
  type: types.CLEAR_AI_FOLLOW_UP_QUESTIONS_DATA,
})

/**
 * Clear AI follow-up answers data
 *
 * @returns {object} Action
 */
export const clearAIFollowUpAnswersData = () => ({
  type: types.CLEAR_AI_FOLLOW_UP_ANSWERS_DATA,
})

/**
 * Set AI follow-up questions visibility
 *
 * @param {boolean} visible - Whether to show AI follow-up questions
 * @returns {object} Action
 */
export const setAIFollowUpQuestionsVisibility = visible => ({
  type: types.SET_AI_FOLLOW_UP_QUESTIONS_VISIBILITY,
  payload: visible,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FETCH_AI_FOLLOW_UP_QUESTIONS_REQUEST: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          busy: true,
          error: null,
        },
      }
    }

    case types.FETCH_AI_FOLLOW_UP_QUESTIONS_SUCCESS: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          busy: false,
          entity: payload,
          error: null,
          visible:
            Array.isArray(payload.additionalQuestionsNeeded) &&
            payload.additionalQuestionsNeeded.length > 0,
        },
      }
    }

    case types.FETCH_AI_FOLLOW_UP_QUESTIONS_ERROR: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          busy: false,
          error:
            payload.message ||
            'An error occurred while fetching AI follow-up questions',
          visible: false,
        },
      }
    }

    case types.SAVE_AI_FOLLOW_UP_ANSWERS_REQUEST: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          savingAnswers: true,
          saveError: null,
        },
      }
    }

    case types.SAVE_AI_FOLLOW_UP_ANSWERS_SUCCESS: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          savingAnswers: false,
          saveError: null,
          visible: false, // Hide questions after successful submission
        },
      }
    }

    case types.SAVE_AI_FOLLOW_UP_ANSWERS_ERROR: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          savingAnswers: false,
          saveError:
            payload.message || 'An error occurred while saving answers',
        },
      }
    }

    case types.FETCH_AI_FOLLOW_UP_ANSWERS_REQUEST: {
      return {
        ...state,
        aiFollowUpAnswers: {
          ...state.aiFollowUpAnswers,
          busy: true,
          error: null,
        },
      }
    }

    case types.FETCH_AI_FOLLOW_UP_ANSWERS_SUCCESS: {
      return {
        ...state,
        aiFollowUpAnswers: {
          ...state.aiFollowUpAnswers,
          busy: false,
          entity: payload,
          error: null,
        },
      }
    }

    case types.FETCH_AI_FOLLOW_UP_ANSWERS_ERROR: {
      return {
        ...state,
        aiFollowUpAnswers: {
          ...state.aiFollowUpAnswers,
          busy: false,
          error: payload.message || 'An error occurred',
        },
      }
    }

    case types.CLEAR_AI_FOLLOW_UP_QUESTIONS_ERROR: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          error: null,
          saveError: null,
        },
      }
    }

    case types.CLEAR_AI_FOLLOW_UP_QUESTIONS_DATA: {
      return {
        ...state,
        aiFollowUpQuestions: {
          busy: false,
          entity: {},
          error: null,
          visible: false,
          savingAnswers: false,
          saveError: null,
        },
      }
    }

    case types.SET_AI_FOLLOW_UP_QUESTIONS_VISIBILITY: {
      return {
        ...state,
        aiFollowUpQuestions: {
          ...state.aiFollowUpQuestions,
          visible: payload,
        },
      }
    }

    case types.CLEAR_AI_FOLLOW_UP_ANSWERS_DATA: {
      return {
        ...state,
        aiFollowUpAnswers: {
          busy: false,
          entity: null,
          error: null,
        },
      }
    }

    default:
      return state
  }
}
